import React, { useRef } from 'react';
import { animated, useTransition } from 'react-spring';
import { useHover } from '@use-gesture/react';
import styled from 'styled-components';
import { ArrowLeftIcon, CrossIcon } from '../../../assets';
import { breakpoint, theme } from '../../../theme';
import { LinkText, UIText } from '../../typography';
import {
  CategoryTileImage,
  CategoryTileContainer,
  CategoryTileLink,
} from './CategoryTile';
import { CategoryListItem, SubMenuItem } from './types';

const Container = styled(animated.div)`
  cursor: default;
  position: fixed;
  top: 72px;
  width: 100vw;
  height: 100vh;
  background-color: ${theme.colors.primary.white};
  z-index: 99;
  top: 0;
  left: 0;
  padding-bottom: ${theme.spacings[3].px};

  ${breakpoint('md')`
        position: absolute;
        min-width: 400px;
        height: auto;
        left: 100%;
        box-shadow: 0px 4px 8px ${theme.colors.neutrals.transparentGrays[6]};
        overflow: hidden;
        width: auto;
        bottom:1px;
    `}
`;

const StyledArrowLeftIcon = styled(ArrowLeftIcon)`
  && {
    width: 24px;
  }
`;

const StyledCrossIcon = styled(CrossIcon)`
  && {
    width: 16px;
    margin-left: auto;
  }
`;

const List = styled.ul`
  list-style-type: none;
  margin-block: unset;
  display: flex;
  flex-direction: column;
  padding: 0 ${theme.spacings[5].rem};
  gap: ${theme.spacings[3].rem};
  max-height: 73%;
  box-sizing: border-box;
  overflow-y: auto;
  ${breakpoint('md')`
  padding-bottom: ${theme.spacings[2].rem};
    max-height: 500px;
    padding-top: 0;
  `}
  width:100%;

  ::-webkit-scrollbar {
    width: 10px;
  }

  &:hover {
    ::-webkit-scrollbar {
      width: 10px;
    }
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.neutrals.transparentGrays[4]};
  }
`;
const ListItem = styled.li`
  ${breakpoint('md')`
    writing-mode: unset;
  `}
`;

const ChildrenContainer = styled.ul`
  list-style-type: none;
  margin-block: unset;
  padding: 0;

  display: flex;
  flex-direction: column;
  margin-left: ${theme.spacings[5].rem};
  margin-top: ${theme.spacings[2].rem};
`;

const StyledLinkText = styled(LinkText).attrs({
  styleType: 'ui',
  isUnderlinedOnHover: true,
})`
  &&& {
    color: ${theme.colors.primary.graysGray};
    text-transform: uppercase;
    padding: ${theme.spacings[2].rem};
  }

  &:hover {
    background: ${theme.colors.neutrals.transparentGrays[8]};
  }
  &:active {
    background: ${theme.colors.neutrals.transparentGrays[7]};
  }
`;

const CategoryName = styled(StyledLinkText)`
  display: inline-block;
  padding: ${theme.spacings[5].rem};
  padding-bottom: 0;
  margin: 0 0 10px 10px;
`;

const mobileAnimations = {
  from: { transform: 'translateX(100vw)' },
  enter: { transform: 'translateX(0vw)' },
  leave: { transform: 'translateX(100vw)' },
};

export type CategorySubMenuProps = {
  items?: SubMenuItem[];
  category: CategoryListItem;
  isOpen: boolean;
  onBack: () => void;
  onClose: (e?: MouseEvent) => void;
  isDesktop?: boolean;
  onClick: (categoryId: string) => void;
};

export const CategorySubMenu: React.FC<CategorySubMenuProps> = ({
  items,
  category,
  isOpen,
  onBack,
  onClose,
  isDesktop = false,
  onClick,
}) => {
  const subMenuTransition = useTransition(
    isOpen && !!items?.length,
    isDesktop ? {} : mobileAnimations
  );

  const domTarget = useRef(null);

  useHover(({ hovering }) => (hovering ? onClick(category.id) : onClick('')), {
    target: domTarget,
    enabled: isDesktop,
  });

  return subMenuTransition(
    (style, item) =>
      item && (
        <Container style={style} key={category.name} ref={domTarget}>
          {!isDesktop && (
            <CategoryTileContainer cursor="default" isSelected>
              <CategoryTileLink as="div">
                <StyledArrowLeftIcon onClick={onBack} />
                <CategoryTileImage
                  dangerouslySetInnerHTML={{ __html: category.icon }}
                />
                <UIText>{category.name}</UIText>
                <StyledCrossIcon onClick={() => onClose()} />
              </CategoryTileLink>
            </CategoryTileContainer>
          )}
          <div>
            <CategoryName size="lg" href={category.url}>
              <strong>{category.name}</strong>
            </CategoryName>
          </div>

          <List>
            {items?.map(({ id, name, url, childrenItems }) => (
              <ListItem key={id}>
                <StyledLinkText size="sm" key={id} href={url}>
                  <strong>{name}</strong>
                </StyledLinkText>
                {!!childrenItems.length && (
                  <ChildrenContainer>
                    {childrenItems.map(({ id, name, url }) => (
                      <StyledLinkText size="sm" key={id} href={url}>
                        {name}
                      </StyledLinkText>
                    ))}
                  </ChildrenContainer>
                )}
              </ListItem>
            ))}
          </List>
        </Container>
      )
  );
};
